import { EARNING_MAP } from './earning-map.generated.js';
import { MAX_PTO_HOURS_PER_DAY } from './pto.js';
/**
 * Trim a payroll item name down to the part a human reads.
 *
 * 'PTO - HOURLY - IT' → 'IT'. The HOURLY/SALARY distinction is a payroll detail
 * that is constant for a given employee, so repeating it on every chip is noise.
 */
export function ptoBucketLabel(name) {
    const trimmed = name
        .replace(/^PTO\s*-\s*(HOURLY|SALARY)\s*-\s*/i, '')
        .replace(/^PTO\s*-\s*/i, '')
        // NetSuite item names carry doubled spaces ('DRIVING  INBOUN').
        .replace(/\s+/g, ' ')
        .trim();
    const base = trimmed === '' ? name.trim() : trimmed;
    if (base !== base.toUpperCase()) {
        // Deliberate casing ('trailer shop') is left exactly as payroll typed it.
        return base;
    }
    // Shouted names read badly next to everything else, but blind title-casing
    // turns IT into 'It' and G&A into 'G&a'. Short tokens are acronyms here —
    // IT, G&A, BWA — so they keep their case and only real words are lowered.
    return base
        .split(' ')
        .map((word) => word.length <= 3 ? word : word.charAt(0) + word.slice(1).toLowerCase())
        .join(' ');
}
/** Round to two decimals; NetSuite returns hours like `5.4167`. */
function round2(value) {
    return Math.round(value * 100) / 100;
}
/**
 * Assemble the four figures.
 *
 * `allotted` and `availableNow` come from the accrual line; `future` from the time
 * entries. Any of the first two being unknown makes every DERIVED figure unknown rather
 * than zero — a confident zero here tells a supervisor somebody has no PTO left, which is
 * the one wrong answer that actually costs somebody their time off.
 */
export function computePtoStanding(allottedHours, availableNowHours, futureEntries, buckets = []) {
    const futureBookedHours = round2(futureEntries.reduce((sum, entry) => sum + (Number.isFinite(entry.hours) ? entry.hours : 0), 0));
    const known = allottedHours !== null && availableNowHours !== null;
    return {
        allottedHours,
        usedHours: known ? round2(allottedHours - availableNowHours) : null,
        futureBookedHours,
        availableIfFutureAppliedHours: availableNowHours === null ? null : round2(availableNowHours - futureBookedHours),
        availableNowHours,
        buckets,
    };
}
/**
 * The bucket a booking in `nsDepartmentId` draws from, or null when the employee
 * holds no PTO accrual for that department.
 *
 * Matching goes through EARNING_MAP — the same (kind, department) → payroll item
 * table the timecard and every amount report use — rather than by parsing the
 * item NAME. Several live names ('DIRECT LABOR', 'YARD TRUCK', 'DRIVING  INBOUN')
 * match no department by text at all, so name-parsing would silently pick the
 * wrong balance for exactly the people hardest to spot it on.
 */
export function ptoBucketForDepartment(standing, nsDepartmentId) {
    if (nsDepartmentId === null || nsDepartmentId === '') {
        return null;
    }
    const mapped = new Set(EARNING_MAP.filter((m) => m.kind === 'pto' && m.nsDepartmentIds.includes(nsDepartmentId)).map((m) => m.nsPayrollItemId));
    return standing.buckets.find((b) => mapped.has(b.nsPayrollItemId)) ?? null;
}
/**
 * Flag a booking that would exhaust or overrun the employee's PTO (Michael, 2026-09-04:
 * "If the entry puts the employee in danger of using all of their hours or going over
 * their allowed hours, I need a flag raised").
 *
 * FLAGS, NEVER BLOCKS — the admin still approves. An `over` row is not refused, it is
 * made impossible to miss.
 *
 * ⚠️ NOT DOUBLE-COUNTING THE BOOKING IS THE WHOLE SUBTLETY, AND THE TWO PATHS DIFFER.
 * "Already counted" means the booking's hours are ALREADY inside the figure being
 * measured against, so adding them again would report a booking as overdrawing by its
 * own size. Where that figure comes from decides the answer:
 *
 *   - BUCKET path — `PtoBucketStanding.futureBookedHours` is summed from OUR
 *     `app.pto_entry` rows (every booking ending today or later, whatever its approval
 *     status). So a SAVED booking is in there whether or not it ever reached NetSuite.
 *   - AGGREGATE path — `PtoStanding.futureBookedHours` is summed from NetSuite's own
 *     future time entries, where only a POSTED booking exists.
 *
 * 🔥 THIS SHIPPED WRONG AND SAID SO OUT LOUD (production, 2026-09-14). The flag used
 * "posted" for both paths, so a saved-but-unposted booking was subtracted twice on the
 * bucket path: an employee with 40 IT hours and one pending 20-hour IT booking was shown
 * a chip reading "IT 20h (20h booked)" — correct — next to a red "PTO nearly used up ...
 * only 0 hours would remain". Every pending booking libelled the balance it was drawn
 * from, and on a 40-hour bucket a single 20-hour request looked like the employee's last.
 */
export function assessPtoStanding(standing, requestedHours, booking) {
    const nsDepartmentId = booking.nsDepartmentId ?? null;
    const bucket = ptoBucketForDepartment(standing, nsDepartmentId);
    // Which "already counted" applies is decided by the path actually taken — see above.
    const alreadyCounted = bucket === null ? booking.posted : booking.saved;
    if (nsDepartmentId !== null && nsDepartmentId !== '' && bucket === null) {
        return {
            verdict: 'unknown',
            projectedRemainingHours: null,
            message: 'This employee holds no PTO accrual for this department in NetSuite, so there is ' +
                'no balance to check the booking against — and nothing for it to post to.',
        };
    }
    const available = bucket === null ? standing.availableIfFutureAppliedHours : bucket.availableIfFutureAppliedHours;
    if (available === null || !Number.isFinite(requestedHours)) {
        return {
            verdict: 'unknown',
            projectedRemainingHours: null,
            message: 'PTO balance could not be read from NetSuite — check the employee’s accrued time ' +
                'there before approving.',
        };
    }
    const projected = round2(alreadyCounted ? available : available - requestedHours);
    if (projected < 0) {
        return {
            verdict: 'over',
            projectedRemainingHours: projected,
            message: `This booking puts the employee ${String(Math.abs(projected))} hours OVER their ` +
                'available PTO, counting everything already logged for future dates.',
        };
    }
    if (projected < MAX_PTO_HOURS_PER_DAY) {
        return {
            verdict: 'low',
            projectedRemainingHours: projected,
            message: `Only ${String(projected)} hours of PTO would remain — less than a full day ` +
                `(${String(MAX_PTO_HOURS_PER_DAY)} hours) — once everything logged is taken.`,
        };
    }
    return {
        verdict: 'ok',
        projectedRemainingHours: projected,
        message: `${String(projected)} hours of PTO would remain once everything logged is taken.`,
    };
}
//# sourceMappingURL=pto-standing.js.map